import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { canRead, type Role } from '@/lib/permissions';
import { LoadingScreen } from '@/components/layout/LoadingScreen';

interface RequireAuthProps {
  children: ReactNode;
  /** If specified, allow only these roles. */
  roles?: Role[];
  /** If specified, the user's role must have READ access on this module. */
  module?: string;
}

export function RequireAuth({ children, roles, module }: RequireAuthProps) {
  const { session, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!session) {
    return <Navigate to="/auth/sign-in" state={{ from: location }} replace />;
  }

  if (!profile) {
    return <Navigate to="/auth/pending" replace />;
  }

  if (!profile.is_active) {
    return <Navigate to="/auth/inactive" replace />;
  }

  if (roles && !roles.includes(profile.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  if (module && !canRead(profile.role, module, profile.section_access)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
